import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Icons } from '../utils/icons';
import api, { API_URL } from '../utils/api';
import { fontStack, btnPrimary, btnSecondary } from '../styles/shared';
import { useToast } from '../contexts/ToastContext';

function FilterDropdown({ value, onChange, placeholder, options }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const selected = options.find(o => o.value === value);

    useEffect(() => {
        if (!open) return;
        const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, [open]);

    return (
        <div ref={ref} style={{ position: 'relative' }}>
            <button
                onClick={() => setOpen(!open)}
                style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                    padding: '7px 12px', border: `1px solid ${value ? 'var(--accent)' : 'var(--border)'}`,
                    borderRadius: '8px', fontSize: '13px', fontWeight: 500,
                    background: value ? 'rgba(0,174,239,0.06)' : 'var(--card-bg)',
                    color: value ? 'var(--accent)' : 'var(--text-secondary)',
                    cursor: 'pointer', fontFamily: fontStack, whiteSpace: 'nowrap',
                    transition: 'all 0.15s',
                }}
            >
                {selected ? selected.label : placeholder}
                <svg width="10" height="6" viewBox="0 0 10 6" fill="none" style={{ transition: 'transform 0.15s', transform: open ? 'rotate(180deg)' : 'none' }}>
                    <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            </button>
            {open && (
                <div style={{
                    position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 50,
                    minWidth: '180px', maxHeight: '240px', overflowY: 'auto',
                    background: 'var(--card-bg)', border: '1px solid var(--border)',
                    borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
                    padding: '4px',
                }}>
                    {/* Reset / All option */}
                    <div
                        onClick={() => { onChange(''); setOpen(false); }}
                        style={{
                            padding: '8px 12px', borderRadius: '6px', fontSize: '13px',
                            fontWeight: !value ? 600 : 400, cursor: 'pointer',
                            color: !value ? 'var(--accent)' : 'var(--text-muted)',
                            background: !value ? 'rgba(0,174,239,0.06)' : 'transparent',
                            transition: 'background 0.1s',
                        }}
                        onMouseEnter={e => { if (value) e.currentTarget.style.background = 'var(--surface-hover)'; }}
                        onMouseLeave={e => { if (value) e.currentTarget.style.background = 'transparent'; }}
                    >
                        All
                    </div>
                    {options.map(opt => (
                        <div
                            key={opt.value}
                            onClick={() => { onChange(opt.value); setOpen(false); }}
                            style={{
                                padding: '8px 12px', borderRadius: '6px', fontSize: '13px',
                                fontWeight: value === opt.value ? 600 : 400, cursor: 'pointer',
                                color: value === opt.value ? 'var(--accent)' : 'var(--text)',
                                background: value === opt.value ? 'rgba(0,174,239,0.06)' : 'transparent',
                                transition: 'background 0.1s',
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            }}
                            onMouseEnter={e => { if (value !== opt.value) e.currentTarget.style.background = 'var(--surface-hover)'; }}
                            onMouseLeave={e => { if (value !== opt.value) e.currentTarget.style.background = 'transparent'; }}
                        >
                            {opt.label}
                            {value === opt.value && (
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function ChattersTab({ wheels, isMobile, onViewEarnings, onStartChat }) {
    const toast = useToast();
    const [chatters, setChatters] = useState([]);
    const [chattersLoading, setChattersLoading] = useState(true);
    const [browseProfiles, setBrowseProfiles] = useState([]);
    const [browseLoading, setBrowseLoading] = useState(true);
    const [pendingRequests, setPendingRequests] = useState([]);
    const [inviteCodes, setInviteCodes] = useState([]);
    const [codeChannels, setCodeChannels] = useState([]);

    // Edit chatter form
    const [editingChatter, setEditingChatter] = useState(null);
    const [editChannels, setEditChannels] = useState([]);
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);

    // Discover filters
    const [searchQuery, setSearchQuery] = useState('');
    const [langFilter, setLangFilter] = useState('');
    const [tzFilter, setTzFilter] = useState('');
    const [pricingFilter, setPricingFilter] = useState('');
    const [sortBy, setSortBy] = useState('recommended');

    const loadAll = useCallback(async () => {
        setChattersLoading(true);
        setBrowseLoading(true);
        try {
            const [chattersRes, browseRes, requestsRes, codesRes] = await Promise.all([
                api.get('/api/admin/chatters').catch(() => ({ data: {} })),
                api.get('/api/admin/chatters/browse').catch(() => ({ data: {} })),
                api.get('/api/admin/chatters/requests').catch(() => ({ data: {} })),
                api.get('/api/admin/chatters/codes').catch(() => ({ data: {} })),
            ]);
            setChatters(chattersRes.data.chatters || []);
            setBrowseProfiles(browseRes.data.profiles || []);
            setPendingRequests(requestsRes.data.requests || []);
            setInviteCodes(codesRes.data.codes || []);
        } catch (e) {}
        setChattersLoading(false);
        setBrowseLoading(false);
    }, []);

    useEffect(() => { loadAll(); }, [loadAll]);

    const respondToRequest = async (requestId, action) => {
        try {
            await api.post('/api/admin/chatters/requests/respond', { request_id: requestId, action, channels: [] });
            loadAll();
        } catch (e) {}
    };

    const generateInviteCode = async () => {
        if (codeChannels.length === 0) return;
        try {
            await api.post('/api/admin/chatters/invite-code', { channels: codeChannels });
            setCodeChannels([]);
            loadAll();
        } catch (e) {}
    };

    const toggleCodeChannel = (ch) => {
        setCodeChannels(prev => prev.includes(ch) ? prev.filter(c => c !== ch) : [...prev, ch]);
    };

    const deleteChatter = async (id, username) => {
        try {
            await api.post('/api/admin/chatters/delete', { chatter_id: id });
            setConfirmDeleteId(null);
            loadAll();
            toast.success(`@${username} removed`);
        } catch (e) { toast.error(e.response?.data?.error || 'Failed to remove chatter'); }
    };

    const openEditChatter = (c) => {
        setEditingChatter(c);
        setEditChannels(c.channels || []);
    };

    const saveChatterChannels = async () => {
        if (!editingChatter) return;
        try {
            await api.post('/api/admin/chatters/update', {
                chatter_id: editingChatter.id,
                channels: editChannels,
            });
            setEditingChatter(null);
            loadAll();
        } catch (e) { toast.error(e.response?.data?.error || 'Failed to save channels'); }
    };

    const removeChatterChannel = async (chatterId, channel, currentChannels) => {
        const newChannels = currentChannels.filter(c => c !== channel);
        try {
            await api.post('/api/admin/chatters/update', { chatter_id: chatterId, channels: newChannels });
            loadAll();
        } catch (e) { toast.error(e.response?.data?.error || 'Failed to update channels'); }
    };

    // Extract unique languages and timezones for filter dropdowns
    const allLanguages = useMemo(() => {
        const langs = new Set();
        browseProfiles.forEach(p => (p.languages || []).forEach(l => langs.add(l.lang)));
        return [...langs].sort();
    }, [browseProfiles]);

    const allTimezones = useMemo(() => {
        const tzs = new Set();
        browseProfiles.forEach(p => { if (p.timezone) tzs.add(p.timezone); });
        return [...tzs].sort();
    }, [browseProfiles]);

    // Filtered and sorted profiles
    const filteredProfiles = useMemo(() => {
        let results = [...browseProfiles];

        // Search
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase().trim();
            results = results.filter(p =>
                (p.display_name || '').toLowerCase().includes(q) ||
                (p.username || '').toLowerCase().includes(q)
            );
        }

        // Language filter
        if (langFilter) {
            results = results.filter(p =>
                (p.languages || []).some(l => l.lang === langFilter)
            );
        }

        // Timezone filter
        if (tzFilter) {
            results = results.filter(p => p.timezone === tzFilter);
        }

        // Pricing type filter
        if (pricingFilter) {
            results = results.filter(p => p.pricing_type === pricingFilter);
        }

        // Sort
        if (sortBy === 'lowest_hourly') {
            results.sort((a, b) => (a.proposed_hourly_rate || 999) - (b.proposed_hourly_rate || 999));
        } else if (sortBy === 'lowest_percentage') {
            results.sort((a, b) => (a.proposed_percentage || 999) - (b.proposed_percentage || 999));
        } else if (sortBy === 'newest') {
            results.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
        }
        // 'recommended' = default API order

        return results;
    }, [browseProfiles, searchQuery, langFilter, tzFilter, pricingFilter, sortBy]);

    const hasActiveFilters = searchQuery || langFilter || tzFilter || pricingFilter;

    const clearFilters = () => {
        setSearchQuery('');
        setLangFilter('');
        setTzFilter('');
        setPricingFilter('');
        setSortBy('recommended');
    };

    const sectionTitle = { fontSize: '16px', fontWeight: 700, color: 'var(--text)', margin: '0 0 16px', display: 'flex', alignItems: 'center', gap: '10px' };
    const card = { background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: '14px', padding: isMobile ? '16px' : '20px', boxShadow: 'var(--card-shadow)', marginBottom: '20px' };

    const sortOptions = [
        { value: 'recommended', label: 'Recommended' },
        { value: 'lowest_hourly', label: 'Lowest hourly' },
        { value: 'lowest_percentage', label: 'Lowest commission' },
        { value: 'newest', label: 'Recently joined' },
    ];
    const pricingOptions = [
        { value: 'hourly', label: 'Hourly' },
        { value: 'percentage', label: 'Percentage' },
        { value: 'both', label: 'Both' },
        { value: 'negotiable', label: 'Negotiable' },
    ];
    const langOptions = useMemo(() => allLanguages.map(l => ({ value: l, label: l })), [allLanguages]);
    const tzOptions = useMemo(() => allTimezones.map(tz => ({ value: tz, label: tz.replace(/_/g, ' ') })), [allTimezones]);

    // Edit Chatter Modal
    if (editingChatter) {
        return (
            <div style={{ maxWidth: '600px', margin: '0 auto' }}>
                <div style={card}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                        <h3 style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text)', margin: 0 }}>Edit Channels — @{editingChatter.username}</h3>
                        <button onClick={() => setEditingChatter(null)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '18px' }}>×</button>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '18px' }}>
                        {wheels.map(w => (
                            <div key={w.channel_username} onClick={() => setEditChannels(prev => prev.includes(w.channel_username) ? prev.filter(c => c !== w.channel_username) : [...prev, w.channel_username])}
                                style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', background: editChannels.includes(w.channel_username) ? 'rgba(0,174,239,0.06)' : 'var(--surface-hover)', border: `1px solid ${editChannels.includes(w.channel_username) ? 'var(--accent)' : 'var(--border)'}`, borderRadius: '8px', cursor: 'pointer', transition: 'all 0.15s' }}>
                                <div style={{ width: '20px', height: '20px', borderRadius: '5px', border: `2px solid ${editChannels.includes(w.channel_username) ? 'var(--accent)' : 'var(--border)'}`, background: editChannels.includes(w.channel_username) ? 'var(--accent)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    {editChannels.includes(w.channel_username) && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>}
                                </div>
                                <p style={{ fontSize: '12px', fontWeight: 500, color: 'var(--text)', margin: 0 }}>{w.title || w.channel_username}</p>
                            </div>
                        ))}
                    </div>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                        <button onClick={() => setEditingChatter(null)} style={btnSecondary}>Cancel</button>
                        <button onClick={saveChatterChannels} style={{ ...btnPrimary, width: 'auto', padding: '10px 20px' }}>Save</button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            {/* ── Pending Requests ── */}
            {pendingRequests.length > 0 && (
                <div style={{ ...card, background: 'rgba(245,158,11,0.04)', borderColor: 'rgba(245,158,11,0.2)' }}>
                    <h3 style={sectionTitle}>
                        <span style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'rgba(245,158,11,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#f59e0b' }}><Icons.Bell /></span>
                        Pending Requests
                        <span style={{ fontSize: '12px', fontWeight: 600, padding: '2px 10px', borderRadius: '10px', background: '#f59e0b', color: '#fff' }}>{pendingRequests.length}</span>
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {pendingRequests.map(req => (
                            <div key={req.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px', background: 'var(--surface-hover)', border: '1px solid var(--border)', borderRadius: '10px' }}>
                                <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'linear-gradient(135deg, #f59e0b, #d97706)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '14px', fontWeight: 700, flexShrink: 0 }}>
                                    {(req.chatter_username || 'C')[0].toUpperCase()}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <p style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text)', margin: 0 }}>@{req.chatter_username}</p>
                                    <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '2px 0 0' }}>Wants to join as your chatter</p>
                                </div>
                                <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                                    <button onClick={() => onStartChat(req.chatter_username, req.display_name)}
                                        style={{ ...btnPrimary, width: 'auto', padding: '8px 18px', fontSize: '12px' }}>Chat</button>
                                    <button onClick={() => respondToRequest(req.id, 'reject')}
                                        style={{ background: 'none', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444', borderRadius: '8px', padding: '8px 18px', fontSize: '12px', cursor: 'pointer', fontFamily: fontStack }}>Reject</button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* ── My Active Chatters ── */}
            <div style={card}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                    <h3 style={{ ...sectionTitle, margin: 0 }}>
                        <span style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'rgba(0,174,239,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent)' }}><Icons.Users /></span>
                        My Chatters
                        {chatters.length > 0 && <span style={{ fontSize: '12px', fontWeight: 500, color: 'var(--text-muted)' }}>({chatters.length})</span>}
                    </h3>
                </div>

                {chattersLoading ? (
                    <div style={{ textAlign: 'center', padding: '40px' }}><img src={'/logo-light.webp'} alt="Loading" style={{ height: '24px', opacity: 0.5, animation: 'pulse 1.5s ease-in-out infinite' }} /></div>
                ) : chatters.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text-muted)' }}>
                        <Icons.Users />
                        <p style={{ marginTop: '12px', fontSize: '14px' }}>No chatters yet</p>
                        <p style={{ fontSize: '12px', marginTop: '4px' }}>Generate an invite code below, or discover available chatters</p>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {chatters.map(c => (
                            <div key={c.id} style={{ display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', gap: '14px', padding: '14px 16px', background: 'var(--surface-hover)', border: '1px solid var(--border)', borderRadius: '12px', flexDirection: isMobile ? 'column' : 'row' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                                    <div style={{ position: 'relative', flexShrink: 0 }}>
                                        <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'linear-gradient(135deg, var(--accent), #0088cc)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '15px', fontWeight: 700 }}>
                                            {(c.display_name || c.username || 'C')[0].toUpperCase()}
                                        </div>
                                        {(() => {
                                            const isOnline = c.last_seen && (Date.now() - new Date(c.last_seen).getTime()) < 5 * 60 * 1000;
                                            return isOnline ? (
                                                <div style={{ position: 'absolute', bottom: 1, right: 1, width: '10px', height: '10px', borderRadius: '50%', background: '#22c55e', border: '2px solid var(--card-bg, #fff)' }} />
                                            ) : null;
                                        })()}
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                            <p style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text)', margin: 0 }}>{c.display_name || c.username}</p>
                                            {c.total_earned_usd > 0 && (
                                                <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '8px', background: 'rgba(16,185,129,0.1)', color: '#10b981', fontWeight: 600 }}>${c.total_earned_usd.toFixed(2)}</span>
                                            )}
                                        </div>
                                        <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '2px 0 0' }}>
                                            {c.last_seen ? (() => {
                                                const isOnline = (Date.now() - new Date(c.last_seen).getTime()) < 5 * 60 * 1000;
                                                if (isOnline) return <span style={{ color: '#22c55e', fontWeight: 500 }}>Online</span>;
                                                const diff = Date.now() - new Date(c.last_seen).getTime();
                                                const mins = Math.floor(diff / 60000);
                                                const hours = Math.floor(mins / 60);
                                                const days = Math.floor(hours / 24);
                                                const ago = days > 0 ? `${days}d ago` : hours > 0 ? `${hours}h ago` : mins > 1 ? `${mins}m ago` : 'just now';
                                                return `Last seen ${ago}`;
                                            })() : '@' + c.username}
                                        </p>
                                        {/* Profile fields: timezone, languages, bio, pricing */}
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px', alignItems: 'center' }}>
                                            {c.timezone && (
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '11px', color: 'var(--text-muted)' }}>
                                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                                                    {c.timezone.replace(/_/g, ' ')}
                                                </span>
                                            )}
                                            {(c.languages || []).map((l, i) => {
                                                const lang = typeof l === 'string' ? l : l.lang;
                                                return <span key={i} style={{ fontSize: '11px', padding: '1px 7px', borderRadius: '4px', background: 'rgba(0,174,239,0.07)', color: 'var(--accent)', fontWeight: 500 }}>{lang}</span>;
                                            })}
                                            {c.pricing_type === 'hourly' && c.proposed_hourly_rate != null && (
                                                <span style={{ fontSize: '11px', padding: '1px 7px', borderRadius: '4px', background: 'rgba(16,185,129,0.08)', color: '#10b981', fontWeight: 600 }}>${c.proposed_hourly_rate}/hr</span>
                                            )}
                                            {c.pricing_type === 'percentage' && c.proposed_percentage != null && (
                                                <span style={{ fontSize: '11px', padding: '1px 7px', borderRadius: '4px', background: 'rgba(245,158,11,0.08)', color: '#f59e0b', fontWeight: 600 }}>{c.proposed_percentage}%</span>
                                            )}
                                            {c.pricing_type === 'both' && (
                                                <>
                                                    {c.proposed_hourly_rate != null && <span style={{ fontSize: '11px', padding: '1px 7px', borderRadius: '4px', background: 'rgba(16,185,129,0.08)', color: '#10b981', fontWeight: 600 }}>${c.proposed_hourly_rate}/hr</span>}
                                                    {c.proposed_percentage != null && <span style={{ fontSize: '11px', padding: '1px 7px', borderRadius: '4px', background: 'rgba(245,158,11,0.08)', color: '#f59e0b', fontWeight: 600 }}>{c.proposed_percentage}%</span>}
                                                </>
                                            )}
                                            {c.pricing_type === 'negotiable' && (
                                                <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Negotiable</span>
                                            )}
                                        </div>
                                        {c.bio && (
                                            <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '4px 0 0', lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                                {c.bio}
                                            </p>
                                        )}
                                        {(c.channels || []).length > 0 && (
                                            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '6px' }}>
                                                {c.channels.map(ch => (
                                                    <span key={ch} onClick={(e) => { e.stopPropagation(); removeChatterChannel(c.id, ch, c.channels); }}
                                                        style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '6px', background: 'rgba(0,174,239,0.08)', color: 'var(--accent)', fontWeight: 500, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px', transition: 'all 0.15s' }}
                                                        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.1)'; e.currentTarget.style.color = '#ef4444'; }}
                                                        onMouseLeave={e => { e.currentTarget.style.background = 'rgba(0,174,239,0.08)'; e.currentTarget.style.color = 'var(--accent)'; }}>
                                                        @{ch} <span style={{ fontSize: '13px', lineHeight: 1 }}>×</span>
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: '6px', flexShrink: 0, flexWrap: 'wrap', ...(isMobile ? { marginLeft: '52px' } : {}) }}>
                                    <button onClick={() => onStartChat(c.username, c.display_name)} style={{ ...btnSecondary, width: 'auto', padding: '7px 14px', fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '5px' }}><Icons.MessageCircle /> Chat</button>
                                    <button onClick={() => onViewEarnings(c)} style={{ ...btnSecondary, width: 'auto', padding: '7px 14px', fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '5px' }}><Icons.Wallet /> Earnings</button>
                                    <button onClick={() => openEditChatter(c)} style={{ ...btnSecondary, width: 'auto', padding: '7px 14px', fontSize: '12px' }}>Edit</button>
                                    {confirmDeleteId === c.id ? (
                                        <>
                                            <button onClick={() => deleteChatter(c.id, c.username)} style={{ background: '#ef4444', border: 'none', color: '#fff', borderRadius: '8px', padding: '7px 14px', fontSize: '12px', cursor: 'pointer', fontFamily: fontStack, fontWeight: 600 }}>Confirm</button>
                                            <button onClick={() => setConfirmDeleteId(null)} style={{ background: 'none', border: '1px solid var(--border)', color: 'var(--text-muted)', borderRadius: '8px', padding: '7px 14px', fontSize: '12px', cursor: 'pointer', fontFamily: fontStack }}>Cancel</button>
                                        </>
                                    ) : (
                                        <button onClick={() => setConfirmDeleteId(c.id)} style={{ background: 'none', border: '1px solid rgba(239,68,68,0.2)', color: '#ef4444', borderRadius: '8px', padding: '7px 14px', fontSize: '12px', cursor: 'pointer', fontFamily: fontStack }}>Remove</button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* ── Invite Codes ── */}
            <div style={card}>
                <h3 style={sectionTitle}>
                    <span style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'rgba(99,102,241,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6366f1' }}><Icons.Link /></span>
                    Invite Codes
                </h3>
                <p style={{ fontSize: '13px', color: 'var(--text-muted)', margin: '0 0 14px' }}>Select channels and generate a single-use code that expires in 12 hours</p>
                {wheels.length === 0 ? (
                    <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Add a channel first.</p>
                ) : (
                    <>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '14px' }}>
                            {wheels.map(w => (
                                <div key={w.channel_username} onClick={() => toggleCodeChannel(w.channel_username)}
                                    style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '20px', cursor: 'pointer', fontSize: '13px', fontWeight: 500, transition: 'all 0.15s', background: codeChannels.includes(w.channel_username) ? 'var(--accent)' : 'var(--surface-hover)', color: codeChannels.includes(w.channel_username) ? '#fff' : 'var(--text-muted)', border: `1px solid ${codeChannels.includes(w.channel_username) ? 'var(--accent)' : 'var(--border)'}` }}>
                                    {w.title || w.channel_username}
                                </div>
                            ))}
                        </div>
                        <button onClick={generateInviteCode} disabled={codeChannels.length === 0} style={{ ...btnPrimary, width: 'auto', padding: '10px 24px', fontSize: '13px', opacity: codeChannels.length === 0 ? 0.5 : 1 }}>Generate Code</button>
                    </>
                )}
                {inviteCodes.length > 0 && (
                    <div style={{ marginTop: '18px', borderTop: '1px solid var(--border)', paddingTop: '16px' }}>
                        <h4 style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)', margin: '0 0 10px' }}>Active Codes</h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {inviteCodes.map(ic => {
                                const isUsed = !!ic.used_by;
                                const isExpired = !isUsed && ic.expires_at && new Date(ic.expires_at) < new Date();
                                return (
                                    <div key={ic.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', background: 'var(--surface-hover)', border: '1px solid var(--border)', borderRadius: '8px', opacity: (isUsed || isExpired) ? 0.5 : 1 }}>
                                        <code style={{ fontSize: '14px', fontWeight: 700, color: 'var(--accent)', letterSpacing: '1px', fontFamily: 'monospace' }}>{ic.code}</code>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                                {(ic.channels || []).map(ch => {
                                                    const wh = wheels.find(w => w.channel_username === ch);
                                                    return <span key={ch} style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '6px', background: 'rgba(0,174,239,0.08)', color: 'var(--accent)' }}>{wh?.title || ch}</span>;
                                                })}
                                            </div>
                                        </div>
                                        {isUsed ? (
                                            <span style={{ fontSize: '11px', color: '#10b981', fontWeight: 500 }}>Used by @{ic.used_by}</span>
                                        ) : isExpired ? (
                                            <span style={{ fontSize: '11px', color: '#ef4444', fontWeight: 500 }}>Expired</span>
                                        ) : (
                                            <span style={{ fontSize: '11px', color: '#f59e0b', fontWeight: 500 }}>Active</span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            {/* ── Discover Available Chatters ── */}
            <div style={{ marginBottom: '20px' }}>
                {/* Section header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                    <h3 style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text)', margin: 0 }}>Discover Chatters</h3>
                    {!browseLoading && (
                        <span style={{ fontSize: '13px', color: 'var(--text-muted)', fontWeight: 500 }}>
                            {filteredProfiles.length} chatter{filteredProfiles.length !== 1 ? 's' : ''} available
                        </span>
                    )}
                </div>

                {/* Filter bar */}
                <div style={{
                    background: 'var(--card-bg)',
                    border: '1px solid var(--card-border)',
                    borderRadius: '12px',
                    padding: isMobile ? '12px' : '14px 18px',
                    marginBottom: '20px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                }}>
                    {/* Top row: search + sort */}
                    <div style={{ display: 'flex', gap: '10px', marginBottom: '10px', flexDirection: isMobile ? 'column' : 'row' }}>
                        <div style={{ position: 'relative', flex: 1 }}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                                <circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>
                            </svg>
                            <input
                                type="text"
                                placeholder="Search name or username..."
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '7px 12px 7px 36px',
                                    border: '1px solid var(--border)',
                                    borderRadius: '8px',
                                    fontSize: '13px',
                                    background: 'var(--input-bg)',
                                    color: 'var(--text)',
                                    fontFamily: fontStack,
                                    outline: 'none',
                                    boxSizing: 'border-box',
                                }}
                            />
                        </div>
                        <FilterDropdown value={sortBy} onChange={setSortBy} placeholder="Sort" options={sortOptions} />
                    </div>

                    {/* Bottom row: dropdown filters */}
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                        {langOptions.length > 0 && (
                            <FilterDropdown value={langFilter} onChange={setLangFilter} placeholder="Language" options={langOptions} />
                        )}
                        {tzOptions.length > 0 && (
                            <FilterDropdown value={tzFilter} onChange={setTzFilter} placeholder="Timezone" options={tzOptions} />
                        )}
                        <FilterDropdown value={pricingFilter} onChange={setPricingFilter} placeholder="Pricing" options={pricingOptions} />

                        {hasActiveFilters && (
                            <button onClick={clearFilters} style={{
                                background: 'none',
                                border: 'none',
                                color: 'var(--accent)',
                                fontSize: '12px',
                                fontWeight: 600,
                                cursor: 'pointer',
                                fontFamily: fontStack,
                                padding: '8px 4px',
                            }}>
                                Clear filters
                            </button>
                        )}
                    </div>
                </div>

                {/* Grid */}
                {browseLoading ? (
                    <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                        <img src={'/logo-light.webp'} alt="Loading" style={{ height: '24px', opacity: 0.5, animation: 'pulse 1.5s ease-in-out infinite' }} />
                    </div>
                ) : filteredProfiles.length === 0 ? (
                    <div style={{
                        textAlign: 'center',
                        padding: '60px 20px',
                        background: 'var(--card-bg)',
                        border: '1px solid var(--card-border)',
                        borderRadius: '12px',
                    }}>
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '16px' }}>
                            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>
                        </svg>
                        <p style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text)', margin: '0 0 6px' }}>No chatters found</p>
                        <p style={{ fontSize: '13px', color: 'var(--text-muted)', margin: 0 }}>
                            {hasActiveFilters ? 'Try adjusting your filters' : 'No available chatters at the moment'}
                        </p>
                        {hasActiveFilters && (
                            <button onClick={clearFilters} style={{
                                marginTop: '16px',
                                padding: '8px 20px',
                                border: '1px solid var(--border)',
                                borderRadius: '8px',
                                background: 'var(--card-bg)',
                                color: 'var(--text)',
                                fontSize: '13px',
                                fontWeight: 500,
                                cursor: 'pointer',
                                fontFamily: fontStack,
                            }}>
                                Clear all filters
                            </button>
                        )}
                    </div>
                ) : (
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(280px, 1fr))',
                        gap: '16px',
                    }}>
                        {filteredProfiles.map(p => (
                            <div key={p.username} style={{
                                background: '#fff',
                                border: '1px solid #e8ecf1',
                                borderRadius: '12px',
                                padding: '20px',
                                display: 'flex',
                                flexDirection: 'column',
                                transition: 'border-color 0.2s, box-shadow 0.2s',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                            }}
                                onMouseEnter={e => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.06)'; }}
                                onMouseLeave={e => { e.currentTarget.style.borderColor = '#e8ecf1'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; }}
                            >
                                {/* Row 1: Avatar + Name + Availability */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
                                    {/* Avatar */}
                                    <div style={{ position: 'relative', flexShrink: 0 }}>
                                        {p.profile_picture ? (
                                            <img
                                                src={`${API_URL}/api/profile/picture?username=${p.username}`}
                                                alt=""
                                                style={{ width: '44px', height: '44px', borderRadius: '50%', objectFit: 'cover' }}
                                                onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                                            />
                                        ) : null}
                                        <div style={{
                                            width: '44px', height: '44px', borderRadius: '50%',
                                            background: '#f1f5f9',
                                            display: p.profile_picture ? 'none' : 'flex',
                                            alignItems: 'center', justifyContent: 'center',
                                            color: '#64748b', fontSize: '16px', fontWeight: 700,
                                        }}>
                                            {(p.display_name || p.username || 'C')[0].toUpperCase()}
                                        </div>
                                    </div>

                                    {/* Name + username */}
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <p style={{ fontSize: '14px', fontWeight: 600, color: '#0f1729', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {p.display_name || p.username}
                                        </p>
                                        <p style={{ fontSize: '12px', color: '#94a3b8', margin: '1px 0 0', fontWeight: 400 }}>@{p.username}</p>
                                    </div>

                                    {/* Availability dot */}
                                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#22c55e', flexShrink: 0, boxShadow: '0 0 0 2px rgba(34,197,94,0.2)' }} title="Available" />
                                </div>

                                {/* Profile fields */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '14px' }}>
                                    {p.timezone && (
                                        <div>
                                            <p style={{ fontSize: '10px', fontWeight: 600, color: '#0f1729', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 4px' }}>Timezone</p>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                                                </svg>
                                                <span style={{ fontSize: '12px', color: '#64748b' }}>{p.timezone.replace(/_/g, ' ')}</span>
                                            </div>
                                        </div>
                                    )}
                                    {(p.languages || []).length > 0 && (
                                        <div>
                                            <p style={{ fontSize: '10px', fontWeight: 600, color: '#0f1729', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 5px' }}>Languages</p>
                                            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                                {p.languages.map((l, i) => (
                                                    <span key={i} style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '4px', background: '#f1f5f9', color: '#475569', fontWeight: 500 }}>
                                                        {l.lang}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {p.bio && (
                                        <div>
                                            <p style={{ fontSize: '10px', fontWeight: 600, color: '#0f1729', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 4px' }}>Bio</p>
                                            <p style={{ fontSize: '12px', color: '#64748b', margin: 0, lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                                {p.bio}
                                            </p>
                                        </div>
                                    )}
                                    <div>
                                        <p style={{ fontSize: '10px', fontWeight: 600, color: '#0f1729', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 5px' }}>Pricing</p>
                                        <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                                            {(p.pricing_type === 'hourly' || p.pricing_type === 'both') && p.proposed_hourly_rate != null && (
                                                <span style={{ fontSize: '12px', padding: '3px 9px', borderRadius: '6px', background: '#f0fdf4', color: '#15803d', fontWeight: 600, border: '1px solid #dcfce7' }}>
                                                    ${p.proposed_hourly_rate}/hr
                                                </span>
                                            )}
                                            {(p.pricing_type === 'percentage' || p.pricing_type === 'both') && p.proposed_percentage != null && (
                                                <span style={{ fontSize: '12px', padding: '3px 9px', borderRadius: '6px', background: '#fffbeb', color: '#a16207', fontWeight: 600, border: '1px solid #fef3c7' }}>
                                                    {p.proposed_percentage}% commission
                                                </span>
                                            )}
                                            {p.pricing_type === 'negotiable' && (
                                                <span style={{ fontSize: '12px', padding: '3px 9px', borderRadius: '6px', background: '#f8fafc', color: '#64748b', fontWeight: 500, border: '1px solid #e2e8f0' }}>
                                                    Negotiable
                                                </span>
                                            )}
                                            {!p.pricing_type && (
                                                <span style={{ fontSize: '12px', padding: '3px 9px', borderRadius: '6px', background: '#f8fafc', color: '#94a3b8', fontWeight: 500, border: '1px solid #e2e8f0' }}>
                                                    Contact for pricing
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Row 5: Actions */}
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <button
                                        onClick={() => onStartChat(p.username, p.display_name)}
                                        style={{
                                            flex: 1,
                                            padding: '9px 16px',
                                            background: '#0f1729',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: '8px',
                                            fontSize: '13px',
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                            fontFamily: fontStack,
                                            transition: 'background 0.15s',
                                        }}
                                        onMouseEnter={e => e.currentTarget.style.background = '#1e293b'}
                                        onMouseLeave={e => e.currentTarget.style.background = '#0f1729'}
                                    >
                                        Message
                                    </button>
                                    <button
                                        onClick={() => onStartChat(p.username, p.display_name)}
                                        style={{
                                            padding: '9px 16px',
                                            background: 'transparent',
                                            color: '#475569',
                                            border: '1px solid #e2e8f0',
                                            borderRadius: '8px',
                                            fontSize: '13px',
                                            fontWeight: 500,
                                            cursor: 'pointer',
                                            fontFamily: fontStack,
                                            transition: 'all 0.15s',
                                        }}
                                        onMouseEnter={e => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.background = '#f8fafc'; }}
                                        onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.background = 'transparent'; }}
                                    >
                                        View Profile
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
